import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { firstValueFrom, map, Observable } from 'rxjs';
import {
  LatestCashRegisterSession,
  NoCashRegisterSession,
} from '../../model/cash-register-session';
import { SuccessResponse } from '../shared/success-response';
import { ICashDrawersApiService } from './cash-drawers-api.interface';
import { CashDrawerApiMapper } from './cash-drawers-api.mapper';
import { FindRegisterResponseDto } from './dtos/find-register.response.dto';
import { CreateRegisterResponseDto } from './dtos/create-register.response.dto';
import { CreateRegisterRequestDto } from './dtos/create-register.request.dto';
import { UpdateRegisterRequestDto } from './dtos/update-register.request.dto';
import { UpdateRegisterResponseDto } from './dtos/update-register.response.dto';
import { FindRegisterBirComplianceResponseDto } from './dtos/find-register-bir-compliance.response.dto';
import { FindRegistersQueryDto } from './dtos/find-registers.query.dto';
import { FindRegistersResponseDto } from './dtos/find-registers.response.dto';
import { FindRegisterInvoiceSeriesResponseDto } from './dtos/find-cash-register-invoice-series.response.dto';
import {
  GatewayDeviceResponseDto,
  type GatewayTransactionsResponseDto,
} from './dtos/gateway-device.response.dto';

const API_URL = `${environment.api}/cash-drawers`;

@Injectable({
  providedIn: 'root',
})
export class CashDrawersApiService implements ICashDrawersApiService {
  private http = inject(HttpClient);

  // Cash register methods
  async findRegisters(
    query: FindRegistersQueryDto,
  ): Promise<FindRegistersResponseDto> {
    const params = new HttpParams({ fromObject: query as any });

    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindRegistersResponseDto>>(`${API_URL}`, {
        params,
      }),
    );

    return response.data;
  }

  async findRegister(id: number): Promise<FindRegisterResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindRegisterResponseDto>>(
        `${API_URL}/${id}`,
      ),
    );

    return response.data;
  }

  async findRegisterBirCompliance(
    id: number,
  ): Promise<FindRegisterBirComplianceResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindRegisterBirComplianceResponseDto>>(
        `${API_URL}/${id}/bir-compliance`,
      ),
    );

    return response.data;
  }

  async findRegisterInvoiceSeries(
    id: number,
  ): Promise<FindRegisterInvoiceSeriesResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindRegisterInvoiceSeriesResponseDto>>(
        `${API_URL}/${id}/invoice-series`,
      ),
    );

    return response.data;
  }

  async findRegisterNames() {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse>(`${API_URL}/names`),
    );
    return response.data;
  }

  async createRegister(
    data: CreateRegisterRequestDto,
  ): Promise<CreateRegisterResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateRegisterResponseDto>>(
        `${API_URL}`,
        data,
      ),
    );

    return response.data;
  }

  async updateRegister(
    registerId: number,
    data: UpdateRegisterRequestDto,
  ): Promise<UpdateRegisterResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdateRegisterResponseDto>>(
        `${API_URL}/${registerId}`,
        data,
      ),
    );

    return response.data;
  }

  deleteRegister(registerId: number): Observable<void> {
    return this.http.delete<void>(`${API_URL}/${registerId}`);
  }

  deleteRegisters(ids: number[]): Observable<void> {
    return this.http.post<void>(`${API_URL}/delete-many`, { registerIds: ids });
  }

  // Gateway device methods
  async registerGatewayDevice(
    registerId: number,
    serialNumber?: string,
  ): Promise<GatewayDeviceResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<GatewayDeviceResponseDto>>(
        `${API_URL}/${registerId}/gateway-device`,
        serialNumber ? { serialNumber } : {},
      ),
    );
    return response.data;
  }

  async getGatewayDevice(
    registerId: number,
  ): Promise<GatewayDeviceResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<GatewayDeviceResponseDto>>(
        `${API_URL}/${registerId}/gateway-device`,
      ),
    );
    return response.data;
  }

  async toggleQrPh(
    registerId: number,
    isQrPhEnabled: boolean,
  ): Promise<GatewayDeviceResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<GatewayDeviceResponseDto>>(
        `${API_URL}/${registerId}/gateway-device/qrph`,
        { isQrPhEnabled },
      ),
    );
    return response.data;
  }

  async getGatewayDeviceTransactions(
    registerId: number,
    pageNo?: number,
    recPerPage?: number,
  ): Promise<GatewayTransactionsResponseDto> {
    const params: Record<string, string> = {};
    if (pageNo !== undefined) params['pageNo'] = String(pageNo);
    if (recPerPage !== undefined) params['recPerPage'] = String(recPerPage);
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<GatewayTransactionsResponseDto>>(
        `${API_URL}/${registerId}/gateway-device/transactions`,
        { params },
      ),
    );
    return response.data;
  }
}
